// Navbar right-side controls: mobile menu, color-mode toggle, profile menu.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Box, Flex, IconButton, Menu, MenuButton, MenuItem, MenuList, Text,
  useColorMode, useColorModeValue
} from "@chakra-ui/react";
import { IoMdMoon, IoMdSunny } from "react-icons/io";
import { FiMenu } from "react-icons/fi";
import { MdPerson } from "react-icons/md";
import NotificationBell from "views/notifications/NotificationBell";

/**
 * Logs the admin out: revokes the session server-side (best-effort), clears the
 * local token and axios auth header, then redirects to the auth screen.
 * @param {Function} navigate - react-router navigate function
 * @param {Function} setLoading - setter to disable the menu during logout
 * @returns {Promise<void>}
 */
async function doLogout(navigate, setLoading) {
  setLoading(true);
  try {
    await axios.delete("users/logout");
  } catch (e) {
    // Session may already be invalid server-side; clear client state regardless.
  }
  localStorage.removeItem("creativeAdminToken");
  axios.defaults.headers.common["Isl-Token"] = null;
  toast.success("Déconnexion réussie");
  navigate("/auth");
}

/**
 * Renders navbar controls: mobile sidebar toggle, light/dark switch and the
 * profile menu (with logout).
 * @param {Object} props - onOpenSidebar callback for the mobile drawer
 * @returns {React.ReactElement}
 */
export default function NavbarLinks({ onOpenSidebar }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { colorMode, toggleColorMode } = useColorMode();
  const navbarIcon = useColorModeValue("gray.400", "white");
  const menuBg = useColorModeValue("white", "navy.800");
  const pillBg = useColorModeValue("rgba(255, 255, 255, 0.7)", "rgba(255, 255, 255, 0.06)");
  const pillBorder = useColorModeValue("rgba(255, 255, 255, 0.5)", "rgba(255, 255, 255, 0.10)");
  const textColor = useColorModeValue("secondaryGray.900", "white");
  return (
    <Flex align="center" gap="4px"
      bg={pillBg} p="5px" borderRadius="30px" border="1px solid" borderColor={pillBorder}
      backdropFilter="blur(12px) saturate(140%)" WebkitBackdropFilter="blur(12px) saturate(140%)"
      boxShadow="0 8px 30px rgba(0, 0, 0, 0.18)">
      <IconButton aria-label="Menu" size="sm" variant="ghost" display={{ base: "flex", xl: "none" }}
        icon={<Box as={FiMenu} fontSize="18px" />} color={navbarIcon} onClick={onOpenSidebar} />
      <NotificationBell />
      <IconButton aria-label="Theme" size="sm" variant="ghost" color={navbarIcon}
        icon={<Box as={colorMode === "light" ? IoMdMoon : IoMdSunny} fontSize="16px" />}
        onClick={toggleColorMode} />
      <Menu>
        <MenuButton p="0px" ml="2px" borderRadius="full" boxShadow="none"
          _hover={{ boxShadow: "none" }} _focus={{ boxShadow: "none" }} _active={{ boxShadow: "none" }}>
          <Flex h="32px" w="32px" borderRadius="full" bg="brand.500" align="center"
            justify="center" overflow="hidden">
            <Box as={MdPerson} color="white" fontSize="20px" />
          </Flex>
        </MenuButton>
        <MenuList bg={menuBg} borderRadius="20px" p="8px" boxShadow="xl">
          <Text px="14px" py="8px" fontSize="sm" fontWeight="700" color={textColor}>
            Administrateur
          </Text>
          <MenuItem borderRadius="8px" color="red.500" fontWeight="500"
            isDisabled={loading} onClick={() => doLogout(navigate, setLoading)}>
            Déconnexion
          </MenuItem>
        </MenuList>
      </Menu>
    </Flex>
  );
}
